// Import files telemetry. Same event names and fields as Android (`ImportTelemetry.kt`,
// PostHogHelper `import_confirm`), so one PostHog breakdown answers a question for every
// platform:
//   - `import_preview` — one per batch of added files, once every file in it has been read:
//     what came in and how the start screen sorted it. On Android this is a BetterStack line;
//     the web has no client→BetterStack path, so it goes to PostHog like any product event.
//   - `import_confirm` — one per press of the import button: what the press creates.
// Low volume: one or two events per import, never one per file or point.

import { counts, crossDestinationCount, currentRoute, fromArchive, group, type ImportFileEntry, type ImportScreenState, willImport } from './importScreenState';

export const IMPORT_PREVIEW_EVENT = 'import_preview';
export const IMPORT_CONFIRM_EVENT = 'import_confirm';

/** Where the screen was opened from: the start screen, as Android's `importEntryName` spells it. */
export type ImportEntryName = 'planned' | 'recordings' | 'waypoints' | 'none';

export function importPreviewProps(entry: ImportEntryName, s: ImportScreenState, batch: ImportFileEntry[]): Record<string, string | number> {
  const sortedDistinct = (xs: Array<string | null | undefined>): string[] =>
    Array.from(new Set(xs.filter((x): x is string => !!x))).sort();
  const props: Record<string, string | number> = {
    entry,
    files: batch.length,
    formats: sortedDistinct(batch.map(e => e.preview?.format)).join(','),
    readyCount: batch.filter(e => group(s, e) === 'ready').length,
    lookCount: batch.filter(e => group(s, e) === 'look').length,
    errorCount: batch.filter(e => group(s, e) === 'error').length
  };
  const errorCodes = sortedDistinct(batch.map(e => e.preview?.error));
  if (errorCodes.length) props.errorCodes = errorCodes.join(',');
  // `files` counts cards and `formats` the files inside a ZIP; this says how many ZIPs there were.
  const archives = new Set(batch.filter(fromArchive).map(e => e.source)).size;
  if (archives > 0) props.archives = archives;
  return props;
}

export function importConfirmProps(entry: ImportEntryName, s: ImportScreenState): Record<string, string | number | boolean> {
  const c = counts(s);
  return {
    entry,
    recordings: c.recordings,
    plannedHikes: c.plannedHikes,
    waypointGroups: c.waypointGroups,
    crossDestination: crossDestinationCount(s),
    fromWaypoints: s.entries.filter(e => willImport(e) && e.settings.choice === 'fromWaypoints').length,
    editInPlanner: c.editInPlanner,
    // Hikes from waypoints left out because their route couldn't be planned.
    routeFailures: s.entries.filter(e => e.settings.choice === 'fromWaypoints' && currentRoute(e)?.status === 'failed').length
  };
}

/** Fire-and-forget through the window.mixpanel facade (PostHog); never lets analytics break an import. */
export function trackImportEvent(event: string, props: Record<string, unknown>): void {
  try {
    (window as any).mixpanel?.track(event, props);
  } catch {
    // analytics must never block an import
  }
}
